import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { pdf, PDFViewer } from '@react-pdf/renderer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import PdfTemplates from '../../../components/PdfTemplates';
import { getBas264Image } from '../../../controllers/V1/cms/generalDataController';
import AgentNavbar from '../components/AgentNavbar';
import AgentLayout from '../components/AgentLayout';
import '../winningVouchers.css';


// Helper function to detect image format and format base64 data URI
const formatImageDataUri = (base64Image, imageUrl = '') => {
  if (!base64Image || typeof base64Image !== 'string') return base64Image;
  
  if (base64Image.startsWith('data:')) return base64Image;
  if (base64Image.startsWith('http')) return base64Image;
  
  let imageType = 'jpeg';
  if (imageUrl) {
    const urlLower = imageUrl.toLowerCase();
    if (urlLower.includes('.png')) {
      imageType = 'png';
    } else if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) {
      imageType = 'jpeg';
    }
  }
  
  if (base64Image.startsWith('iVBORw0KGgo')) {
    imageType = 'png';
  } else if (base64Image.startsWith('/9j/')) {
    imageType = 'jpeg';
  }
  
  return `data:image/${imageType};base64,${base64Image}`;
};

const processImageField = async (imageData, fieldName) => {
  if (!imageData) return null;
  
  try {
    const imageUrl = typeof imageData === 'string' 
      ? imageData 
      : imageData[fieldName] || imageData;
    
    if (!imageUrl) return null;
    
    const base64Image = await getBas264Image({image_url: imageUrl});
    
    if (base64Image && typeof base64Image === 'object' && base64Image.status === false) {
      console.error(`Failed to fetch ${fieldName} image:`, base64Image.message);
      return null;
    }
    
    if (base64Image && typeof base64Image === 'string') {
      const formattedImage = formatImageDataUri(base64Image, imageUrl);
      
      return {
        ...(typeof imageData === 'object' ? imageData : {}),
        [fieldName]: formattedImage,
      };
    }
    
    return null;
  } catch (error) {
    console.error(`Error processing ${fieldName} image:`, error);
    return null;
  }
};

export const processFormDataImages = async (formData) => {
  if (!formData) return formData;
  
  const processedData = { ...formData };
  
  if (processedData.doc) {
    const processedDoc = await processImageField(processedData.doc, 'doc');
    if (processedDoc) {
      processedData.doc = processedDoc;
    }
  }
  
  if (processedData.order_slip) {
    const processedOrderSlip = await processImageField(processedData.order_slip, 'order_slip');
    if (processedOrderSlip) {
      processedData.order_slip = processedOrderSlip;
    }
  }
  
  if (processedData.redeem_slip) {
    const processedRedeemSlip = await processImageField(processedData.redeem_slip, 'redeem_slip');
    if (processedRedeemSlip) {
      processedData.redeem_slip = processedRedeemSlip;
    }
  }
  
  if (processedData.other_attachment) {
    const processedOtherAttachment = await processImageField(processedData.other_attachment, 'other_attachment');
    if (processedOtherAttachment) {
      processedData.other_attachment = processedOtherAttachment;
    }
  }

  if (processedData.signature) {
    const processedsignature = await processImageField(processedData.signature, 'signature');
    if (processedsignature) {
      processedData.signature = processedsignature;
    }
  }

  if (processedData.images) {
    const processedImages = await processImageField(processedData.images, 'images');
    if (processedImages) {
      processedData.images = processedImages;
    }
  }

  if (processedData.images2) {
    const processedImages2 = await processImageField(processedData.images2, 'images2');
    if (processedImages2) {
      processedData.images2 = processedImages2;
    }
  }
  
  return processedData;
};

const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.innerWidth <= 768);
};

const PreviewPDF = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const voucherData = location?.state;
  const [processedData, setProcessedData] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const pdfBlobUrlRef = useRef(null);

  useEffect(() => {
    document.title = "U-Winn || Voucher PDF";
    const token = sessionStorage.getItem("AGENT-TOKEN");
    const user = JSON.parse(sessionStorage.getItem("AGENT-ADMIN-INFO") || "{}");
    if (!token || !user?._id) {
      navigate("/agent-login", { replace: true });
    }
    const onResize = () => setIsMobile(isMobileDevice());
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [navigate]);

  useEffect(() => {
    processFormDataImages(voucherData).then((data) => {
      setProcessedData(data);
    });
  }, [voucherData]);

  useEffect(() => {
    if (isMobile && processedData) {
      const generatePdfBlob = async () => {
        try {
          if (pdfBlobUrlRef.current) {
            URL.revokeObjectURL(pdfBlobUrlRef.current);
            pdfBlobUrlRef.current = null;
          }

          const doc = <PdfTemplates voucherData={processedData || voucherData} />;
          const asPdf = pdf(doc);
          const blob = await asPdf.toBlob();
          const url = URL.createObjectURL(blob);
          pdfBlobUrlRef.current = url;
          setPdfBlobUrl(url);
        } catch (error) {
          console.error('Error generating PDF blob:', error);
        }
      };
      generatePdfBlob();
    }

    return () => {
      if (pdfBlobUrlRef.current) {
        URL.revokeObjectURL(pdfBlobUrlRef.current);
        pdfBlobUrlRef.current = null;
      }
    };
  }, [isMobile, processedData, voucherData]);

  if (!voucherData) {
    return (
      <AgentLayout>
        <AgentNavbar title="Voucher Preview" />
        <div className="agent-card agent-empty-state">No voucher data available</div>
      </AgentLayout>
    );
  }

  const fileName = `Winning_Voucher_${processedData?.order_no || voucherData?.order_no || ''}_${moment(new Date()).format('DD-MM-YYYY')}.pdf`;

  return (
    <AgentLayout className="agent-preview-page">
      <AgentNavbar title="Voucher Preview" />
      <div className="agent-preview-panel">
        <div className="agent-preview-toolbar">
          <button
            type="button"
            className="btn btn-agent-outline"
            onClick={() => navigate("/voucher-list")}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
          {isMobile && pdfBlobUrl && (
            <a
              href={pdfBlobUrl}
              download={fileName}
              className="btn btn-agent-primary"
            >
              <FontAwesomeIcon icon={faDownload} />
              Download PDF
            </a>
          )}
        </div>
        <div className="agent-preview-content">
          {isMobile ? (
            pdfBlobUrl ? (
              <iframe src={pdfBlobUrl} title="PDF Viewer" />
            ) : (
              <div className="agent-preview-loading">
                <FontAwesomeIcon icon={faSpinner} spin />
                <p>Generating PDF...</p>
              </div>
            )
          ) : (
            <PDFViewer
              style={{
                width: '100%',
                height: 'calc(100vh - 180px)',
                minHeight: '560px',
                border: 'none',
              }}
            >
              <PdfTemplates voucherData={processedData || voucherData} />
            </PDFViewer>
          )}
        </div>
      </div>
    </AgentLayout>
  );
};

export default PreviewPDF;
